import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import ApiService from '../_services/ApiService';

/* ── LM Builder config ── */

export interface LmBuilderConfig {
  lessonPlanTypeRestrictTo?: 'learning_module' | 'lesson_plan';
  flipClassEnabled?: number; // 1=Yes, 0=No
  aiQuizEnabled?: boolean;
  aiQuizQuestionCount?: number;
}

export interface SchedulingTimeSlotPreset {
  label: string;
  startTime: string; // 'HH:MM' 24-hour format
  endTime: string;   // 'HH:MM' 24-hour format
}

export interface SchedulingPresets {
  timeSlots: SchedulingTimeSlotPreset[];
}

/** Mark Foil feature config (merged in from customer_detail_master.JsonConfig.markFoil). */
export interface MarkFoilConfig {
  /** 1 = feature enabled (dropdown item shown, server accepts requests); anything else = disabled. */
  enableStatus?: number;
  /** template_master.Id (must have Type = 4). When unset, the controller falls back to any active Type=4 template. */
  templateId?: number;
  universityName?: string;
  universitySubtitle?: string;
  universityAddress?: string;
}

/** Top-level iTeachAppConfig shape */
export interface ITeachAppConfig {
  lmBuilder?: LmBuilderConfig;
  bloomsTagTypeId?: number;
  logoUrl?: string;
  displayName?: string;
  poweredByName?: string;
  hidePoweredBy?: boolean;
  schedulingPresets?: SchedulingPresets;
  /** Merged in by the backend from customer_detail_master.JsonConfig.markFoil — same field name. */
  markFoil?: MarkFoilConfig;
  /** True when resource_master.Id=20240 EnableStatus=1 (program-based tenant). */
  isProgramBased?: boolean;
  /** Merged in by the backend from customer_detail_master.JsonConfig.testAttribute. */
  testAttribute?: TestAttributeConfig;
  /** Merged in by the backend from customer_detail_master.JsonConfig.test. */
  test?: TestConfig;
  /** Merged in by the backend from customer_detail_master.JsonConfig.question. */
  question?: QuestionAttributeConfig;
  /** Merged in by the backend from customer_detail_master.JsonConfig.digitalEvaluation. */
  digitalEvaluation?: DigitalEvaluationConfig;
  /**
   * Lesson Plan Coverage Report visibility toggles (default-on: absent/true =
   * shown). `attendanceColumns` refines the Attendance_Sessions group per column;
   * the group master wins when it is off.
   */
  lessonPlanCoverageReport?: {
    LessonPlan_Coverage?: boolean;
    Attendance_Sessions?: boolean;
    attendanceColumns?: Record<string, boolean>;
  };
  /** Fee / installment template screen options. */
  feeTemplate?: {
    /** Show the per-record Multiplier column on the fee template edit form (default off). */
    showInstallmentMultiplier?: boolean;
  };
}

/**
 * Gating for the question-view extra blocks (Question Doc, Solution Video,
 * Rubric, Reviews). Stored in customer_detail_master.JsonConfig.question.
 * Root users always see + edit every block regardless of this config.
 * For non-root users, a block is shown only when `enableStatus === 1` AND the
 * user holds one of `viewRoles` (empty/absent viewRoles = visible to all enabled
 * users). Edit controls appear only when the user holds one of `editRoles`.
 */
export interface QuestionAttributeBlockConfig {
  enableStatus?: number;     // 1 = enabled
  viewRoles?: number[];      // role IDs allowed to view (empty/absent = all)
  editRoles?: number[];      // role IDs allowed to edit (empty/absent = none, i.e. root-only)
}

export interface QuestionAttributeConfig {
  questionDoc?: QuestionAttributeBlockConfig;
  solutionVideo?: QuestionAttributeBlockConfig;
  rubric?: QuestionAttributeBlockConfig;
  reviews?: QuestionAttributeBlockConfig;
}

/**
 * Digital Evaluation (DVS) gating for the "Digital Evaluation Allocation" block
 * on the Test View. Stored in customer_detail_master.JsonConfig.digitalEvaluation.
 * The block is shown only when `enableStatus === 1` AND the test's category has
 * `needsManualEvaluation` AND the evaluation type is allowed AND the user holds a
 * matching role. Root/admin users always pass the role check. Upload roles gate
 * the "Upload Scanned Test Paper" button; manage roles gate "Manage Digital
 * Paper" + "Uploaded Scanned Paper".
 */
export interface DigitalEvaluationConfig {
  enableStatus?: number;   // 1 = enabled
  uploadRoles?: number[];  // role IDs allowed to upload scanned papers
  manageRoles?: number[];  // role IDs allowed to manage / view uploaded papers
}

/**
 * Quick Quiz results column config value. Either a bare boolean (legacy,
 * enabled-only) or an object carrying both the on/off state and a per-tenant
 * display name for the column header.
 */
export type TestViewResultColumn = boolean | { enabled?: boolean; label?: string };

/** Test-related UI gating (from customer_detail_master.JsonConfig.testAttribute). */
export interface TestAttributeConfig {
  /**
   * Quick Quiz results column visibility. Maps a column key to whether it is
   * shown. Columns absent from the map (or set to false) are hidden — except
   * the always-on static columns (Student Name, Code, Marks).
   */
  testViewResult?: Record<string, TestViewResultColumn>;
}

/** Test block config (from customer_detail_master.JsonConfig.test). */
export interface TestConfig {
  /**
   * Quick Quiz results column config. Maps a column key to its enabled state
   * and (optionally) a tenant-specific display name — e.g. renaming the
   * `schoolRank` column header to "Centre Rank".
   *
   * The reserved `configRoleIds` key (a number[]) lists the roles allowed to
   * open the "Columns" configuration modal. Root/admin can always open it,
   * regardless of this list.
   */
  testViewResult?: Record<string, TestViewResultColumn | number[]>;
}

/* ── Context ── */

interface ITeachConfigContextType {
  config: ITeachAppConfig;
  loading: boolean;
}

export const ITeachConfigContext = createContext<ITeachConfigContextType>({
  config: {},
  loading: true,
});

export const useITeachConfig = () => useContext(ITeachConfigContext);

/* ── Provider ── */

interface Props {
  children: ReactNode;
}

export const ITeachConfigProvider: React.FC<Props> = ({ children }) => {
  const [config, setConfig] = useState<ITeachAppConfig>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await ApiService.getIteachAppConfig();
        const data = res.data as { success: boolean; config?: ITeachAppConfig };
        setConfig(data.config || {});
      } catch (err) {
        console.error('[ITeachConfigProvider] Failed to load iTeach config:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <ITeachConfigContext.Provider value={{ config, loading }}>
      {children}
    </ITeachConfigContext.Provider>
  );
};
